import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Switch,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Button,
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

// Swiper CSS
import 'swiper/css';
import 'swiper/css/pagination';

// Custom Swiper Styles
const swiperStyles = `
.swiper-pagination {
    bottom: 10px !important;
    position: relative !important;
    margin-top: 20px !important;
}

.swiper-pagination-bullet {
    width: 10px !important;
    height: 10px !important;
    background: rgba(0, 0, 0, 0.2) !important;
    opacity: 1 !important;
    margin: 0 4px !important;
    border-radius: 50% !important;
}

.swiper-pagination-bullet-active {
    background: #FF4B6B !important;
    transform: scale(1.2) !important;
}

.swiper-pagination-bullet:hover {
    background: #FF4B6B !important;
    opacity: 0.7 !important;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = swiperStyles;
    document.head.appendChild(styleSheet);
}
import { styled } from '@mui/material/styles';
import PricingCard, { PricingPlan } from './PricingCard';
import { getUserSubscription } from '../../../services/billing.service';
import CheckIcon from '@mui/icons-material/Check';
import InfinityIcon from '@mui/icons-material/AllInclusive';
import { TopNavigation } from '../Home/MainPage2';
import Footer from '../Home/Footer';
import HomeContainer from '../Home/HomeContainer';
import { getFittingProducts, FittingProductItem } from '../../../utils/subscriptionApi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVendorStore } from '../../../store/vendorStore';
import { useVendorLoginDialogStore } from '../../../store/vendorLoginDialogStore';
import { useFullScreenDialogStore } from '../../../store/fullScreenDialogStore';
import SubscriptionCheckoutDialog from '../../../components/Dialog/SubscriptionCheckoutDialog';

const HeroSection = styled(Box)(({ theme }) => ({
    background: '#FFFFFF',
    color: '#1F2937',
    padding: '60px 0 20px 0',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
        padding: '40px 0 15px 0',
    },
}));

const PricingSection = styled(Box)(({ theme }) => ({
    padding: '40px 0 180px 0',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('md')]: {
        padding: '30px 0 50px 0',
    },
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
    padding: '40px 0',
    [theme.breakpoints.down('md')]: {
        padding: '30px 0',
    },
}));

const BillingToggle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '40px',
    padding: '8px 16px',
    border: '2px solid #E5E7EB',
    borderRadius: '50px',
    backgroundColor: '#F9FAFB',
    maxWidth: '400px',
    margin: '0 auto 40px auto',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'row', // 한 줄로 유지
        gap: '8px',
        padding: '6px 10px',
        maxWidth: '280px',
    },
}));

const SaveBadge = styled(Box)(({ theme }) => ({
    backgroundColor: '#FF4B6B',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
        padding: '2px 8px',
        fontSize: '10px',
        borderRadius: '8px',
    },
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    padding: '0 20px',
    [theme.breakpoints.down('sm')]: {
        padding: '0 16px',
    },
}));

const SubscriptionPage: React.FC = () => {
    const [isYearly, setIsYearly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [allPlans, setAllPlans] = useState<PricingPlan[]>([]);
    const [filteredPlans, setFilteredPlans] = useState<PricingPlan[]>([]);
    const [swiperInstance, setSwiperInstance] = useState<any>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { vendorId } = useVendorStore();
    const [activeProductId, setActiveProductId] = useState<string | null>(null);
    const [activePlanPrice, setActivePlanPrice] = useState<number | null>(null);
    const { setIsOpen: openVendorLoginDialog } = useVendorLoginDialogStore();
    const { action: fullScreenDialogAction } = useFullScreenDialogStore();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        console.log('filteredPlans :>> ', filteredPlans);
    }, [filteredPlans]);

    // API 데이터를 UI 카드 형태로 변환
    const mapApiToPricingPlans = (items: FittingProductItem[] | null | undefined): PricingPlan[] => {
        if (!items || !Array.isArray(items)) {
            return [];
        }
        const mapped = items
            .flatMap((item) => {
                if (!item) {
                    return [];
                }

                // fittingSubscriptionPlans가 있고 비어있지 않은 경우 (월간/연간 정보가 있는 경우)
                if (
                    item.fittingSubscriptionPlans &&
                    Array.isArray(item.fittingSubscriptionPlans) &&
                    item.fittingSubscriptionPlans.length > 0
                ) {
                    return item.fittingSubscriptionPlans.map((plan) => {
                        const isYear = plan.billingCycle === 'yearly';
                        return {
                            id: item.id,
                            title: item.name || '',
                            description: item.description || '',
                            price: item.totalCharge,
                            period: isYear ? ' / 년' : ' / 월',
                            isPopular:
                                item.name?.includes('프리미엄') ||
                                item.name?.toLowerCase().includes('premium') ||
                                false,
                            features: (item.features && Array.isArray(item.features) ? item.features : []).map(
                                (group) => ({
                                    title: group.title || '',
                                    features: (group.features || []).map((f) => ({
                                        text: f?.text || '',
                                        isIncluded: !!f?.isIncluded,
                                        isAddOn: !!f?.isAddOn,
                                    })),
                                }),
                            ),
                            buttonText: '구독하기',
                            buttonVariant:
                                isYear &&
                                (item.name?.includes('프리미엄') || item.name?.toLowerCase().includes('premium'))
                                    ? 'primary'
                                    : 'secondary',
                        } as PricingPlan;
                    });
                }

                // fittingSubscriptionPlans가 빈 배열이거나 없는 경우 (연간 플랜으로 추정)
                // 같은 이름의 플랜이 있고 월간 플랜이 있는지 확인하여 연간 플랜인지 판단
                const hasMonthlyPlan = items.some(
                    (otherItem) =>
                        otherItem?.name === item.name &&
                        otherItem?.fittingSubscriptionPlans?.some((p) => p.billingCycle === 'monthly'),
                );

                // 월간 플랜이 있는데 현재 아이템에 fittingSubscriptionPlans가 없다면 연간 플랜으로 처리
                if (hasMonthlyPlan) {
                    return [
                        {
                            id: item.id,
                            title: item.name || '',
                            description: item.description || '',
                            price: item.totalCharge,
                            period: ' / 년',
                            isPopular:
                                item.name?.includes('프리미엄') ||
                                item.name?.toLowerCase().includes('premium') ||
                                false,
                            features: (item.features && Array.isArray(item.features) ? item.features : []).map(
                                (group) => ({
                                    title: group.title || '',
                                    features: (group.features || []).map((f) => ({
                                        text: f?.text || '',
                                        isIncluded: !!f?.isIncluded,
                                        isAddOn: !!f?.isAddOn,
                                    })),
                                }),
                            ),
                            buttonText: '구독하기',
                            buttonVariant:
                                item.name?.includes('프리미엄') || item.name?.toLowerCase().includes('premium')
                                    ? 'primary'
                                    : 'secondary',
                        } as PricingPlan,
                    ];
                }

                // 월간 플랜도 없는 경우 (크레딧 상품 등)는 제외
                return [];
            })
            .filter(Boolean);

        // 맞춤형 플랜 추가 (항상 표시)
        const businessPlan: PricingPlan = {
            id: 'business',
            title: '맞춤형',
            description: '회사 요구사항에 맞는 확장 가능한 플랜',
            price: 'Custom',
            features: [
                {
                    title: '맞춤 서비스',
                    features: [
                        { text: '프리미엄의 모든 기능', isIncluded: true, isAddOn: true },
                        { text: 'AI 피팅 및 재작업 요청 가격 협의', isIncluded: true, isAddOn: true },
                        { text: '사입 상품 옷 수거', isIncluded: true, isAddOn: true },
                        { text: '상품 사진 촬영 서비스', isIncluded: true, isAddOn: true },
                    ],
                },
            ],
            buttonText: '문의하기',
            buttonVariant: 'secondary',
        };

        return [...mapped, businessPlan];
    };

    // 사용자 활성 구독 정보 로드
    React.useEffect(() => {
        const fetchActiveSubscription = async () => {
            try {
                if (!vendorId) return;
                const res = await getUserSubscription(vendorId);
                const productId = res?.item?.product?.id;
                if (productId) {
                    setActiveProductId(productId);
                    // 활성 플랜의 가격 정보도 저장
                    const activePlan = allPlans.find((plan) => plan.id === productId);
                    if (activePlan && typeof activePlan.price === 'number') {
                        setActivePlanPrice(activePlan.price);
                    }
                }
            } catch {
                setActiveProductId(null);
                setActivePlanPrice(null);
            }
        };
        fetchActiveSubscription();
    }, [vendorId, allPlans]);

    // 구독 상품 로드 (한 번만)
    React.useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                // /promo 경로일 때는 filter 없이 호출, 그 외에는 filter 포함
                const query = location.pathname === '/promo' ? undefined : 'filter=isPublic||$eq||t';
                const response = await getFittingProducts(query);
                // 응답이 null이거나 data 속성이 없거나 null인 경우 처리
                const productsData = response?.data || null;
                setAllPlans(mapApiToPricingPlans(productsData));
            } catch (e: any) {
                setError(e?.message || '구독 상품을 불러오지 못했습니다.');
                setAllPlans([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [location.pathname]);

    // 월간/연간 토글에 따른 필터링
    React.useEffect(() => {
        const filtered = allPlans.filter((plan) => {
            if (plan.id === 'business') return true; // 맞춤형 플랜은 항상 표시
            return isYearly ? plan.period === ' / 년' : plan.period === ' / 월';
        });
        setFilteredPlans(filtered);
    }, [allPlans, isYearly]);

    const featureComparisonData = [
        {
            feature: '이미지 업스케일 (향상)',
            description: '이미지 해상도 증가 및 품질 향상',
            free: true,
            essentials: true,
            pro: true,
            business: true,
        },
        {
            feature: '크기 조정 및 자르기',
            description: '필요에 맞게 이미지 크기 조정',
            free: true,
            essentials: true,
            pro: true,
            business: true,
        },
        {
            feature: '배경 제거',
            description: '배경에서 제품을 분리',
            free: true,
            essentials: true,
            pro: true,
            business: true,
        },
    ];

    // 플랜 다운그레이드 방지 함수
    const isDowngrade = (planId: string): boolean => {
        if (!activePlanPrice || planId === 'business') return false;

        const targetPlan = allPlans.find((plan) => plan.id === planId);
        if (!targetPlan || typeof targetPlan.price !== 'number') return false;

        return targetPlan.price < activePlanPrice;
    };

    const handleSubscribe = (planId: string) => {
        // 맞춤형 플랜(문의하기) 버튼 클릭 시 카카오톡 채널로 이동
        if (planId === 'business') {
            window.open('http://pf.kakao.com/_BxfaRn/chat', '_blank');
            return;
        }

        // 비회원 구독 차단: 로그인 필요
        if (!vendorId) {
            openVendorLoginDialog(true);
            return;
        }

        // 다운그레이드 방지
        if (isDowngrade(planId)) {
            return;
        }

        const plan = filteredPlans.find((p) => p.id === planId);
        if (plan) {
            // 체크아웃 다이얼로그 열기
            fullScreenDialogAction.setTitle('구독 결제');
            fullScreenDialogAction.setPlanData({
                planId: plan.id,
                planTitle: plan.title,
                planPrice: plan.price,
                planPeriod: plan.period,
            });
            fullScreenDialogAction.open();
        }
    };

    const renderFeatureTable = () => (
        <TableContainer component={Paper} sx={{ marginTop: '20px', borderRadius: '12px' }}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                        <TableCell sx={{ fontWeight: 600, width: '25%' }}>주요 기능</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: '18.75%' }}>
                            무료 체험
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: '18.75%' }}>
                            Essential
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: '18.75%' }}>
                            Pro
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: '18.75%' }}>
                            Business
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {featureComparisonData.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <Box>
                                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{row.feature}</Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
                                        {row.description}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                {row.free ? (
                                    <CheckIcon sx={{ color: '#10B981', fontSize: 20 }} />
                                ) : (
                                    <Typography sx={{ color: 'text.disabled' }}>-</Typography>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                {row.essentials ? (
                                    <CheckIcon sx={{ color: '#10B981', fontSize: 20 }} />
                                ) : (
                                    <Typography sx={{ color: 'text.disabled' }}>-</Typography>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                {row.pro ? (
                                    <CheckIcon sx={{ color: '#10B981', fontSize: 20 }} />
                                ) : (
                                    <InfinityIcon sx={{ color: '#FF4B6B', fontSize: 20 }} />
                                )}
                            </TableCell>
                            <TableCell align="center">
                                {row.business ? (
                                    <InfinityIcon sx={{ color: '#FF4B6B', fontSize: 20 }} />
                                ) : (
                                    <Typography sx={{ color: 'text.disabled' }}>-</Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <HomeContainer>
            <TopNavigation />
            {/* Hero Section */}
            <HeroSection>
                <Container maxWidth="xl">
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '35px', md: '56px' },
                            fontWeight: 700,
                            marginBottom: '20px',
                            lineHeight: 1.1,
                            color: '#1F2937',
                        }}
                    >
                        AI로 완성하는 제품 사진
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '14px', md: '20px' },
                            color: 'rgba(0, 0, 0, 0.6)',
                            margin: '0 auto',
                        }}
                    >
                        샵쓰리는 오케스트레이션 방식을 적용해 피팅을 진행합니다. <br />
                        여러 AI 공정을 정교하게 조율하여 더욱 완성도 높은 결과를 만들어 드립니다.
                    </Typography>
                </Container>
            </HeroSection>

            {/* Pricing Section */}
            <PricingSection>
                <Container maxWidth="xl" sx={{ paddingTop: '20px' }}>
                    <BillingToggle>
                        <Typography
                            sx={{
                                color: isYearly ? 'text.secondary' : 'text.primary',
                                fontWeight: 500,
                                fontSize: { xs: '12px', md: '14px' },
                            }}
                        >
                            월간 결제
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isYearly}
                                    onChange={(e) => setIsYearly(e.target.checked)}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#FF4B6B',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#FF4B6B',
                                        },
                                    }}
                                />
                            }
                            label=""
                            sx={{
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                    margin: 0,
                                },
                            }}
                        />
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography
                                sx={{
                                    color: isYearly ? 'text.primary' : 'text.secondary',
                                    fontWeight: 500,
                                    fontSize: { xs: '12px', md: '14px' },
                                }}
                            >
                                연간 결제
                            </Typography>
                            <SaveBadge>10% 할인</SaveBadge>
                        </Stack>
                    </BillingToggle>

                    {isMobile ? (
                        // 모바일: 캐러셀
                        <CarouselContainer sx={{ paddingTop: '30px' }}>
                            {loading && (
                                <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                    구독 상품을 불러오는 중...
                                </Typography>
                            )}
                            {error && <Typography sx={{ color: '#EF4444', textAlign: 'center' }}>{error}</Typography>}
                            {!loading && !error && (
                                <>
                                    <Swiper
                                        modules={[Pagination]}
                                        spaceBetween={20}
                                        slidesPerView={1.1}
                                        centeredSlides={false}
                                        pagination={{
                                            clickable: true,
                                            dynamicBullets: false,
                                        }}
                                        onSwiper={setSwiperInstance}
                                        style={{
                                            padding: '0 0 60px 0',
                                        }}
                                    >
                                        {filteredPlans.map((plan) => (
                                            <SwiperSlide key={plan.id}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <PricingCard
                                                            plan={plan}
                                                            onSubscribe={handleSubscribe}
                                                            isCurrentPlan={activeProductId === plan.id}
                                                            isDowngrade={isDowngrade(plan.id)}
                                                        />
                                                    </Box>
                                                </Box>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </>
                            )}
                        </CarouselContainer>
                    ) : (
                        // 데스크톱: 기존 레이아웃
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: { xs: 2, md: 3 },
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                paddingTop: '30px',
                            }}
                        >
                            {loading && (
                                <Typography sx={{ color: 'text.secondary' }}>구독 상품을 불러오는 중...</Typography>
                            )}
                            {error && <Typography sx={{ color: '#EF4444' }}>{error}</Typography>}
                            {!loading &&
                                !error &&
                                filteredPlans.map((plan) => (
                                    <Box
                                        key={plan.id}
                                        sx={{
                                            flex: {
                                                xs: '1 1 100%',
                                                sm: '1 1 calc(50% - 12px)',
                                                md: '1 1 calc(25% - 18px)',
                                                lg: '1 1 calc(25% - 18px)',
                                            },
                                            minWidth: { xs: 'auto', md: '280px' },
                                            maxWidth: { xs: 'auto', sm: '400px', md: '350px' },
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <PricingCard
                                                plan={plan}
                                                onSubscribe={handleSubscribe}
                                                isCurrentPlan={activeProductId === plan.id}
                                                isDowngrade={isDowngrade(plan.id)}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Container>
            </PricingSection>
            <Footer />

            {/* Subscription Checkout Dialog */}
            <SubscriptionCheckoutDialog />
        </HomeContainer>
    );
};

export default SubscriptionPage;
